import { PayloadAction } from "@reduxjs/toolkit";
import { put, takeLatest, call, all } from "redux-saga/effects";

import { BannerStatus } from "@plex/react-components";

import { HttpStatusCodes } from "infrastructure/http";
import { commonMessages } from "infrastructure/localization";
import { notification } from "infrastructure/notification";
import { displayMessages } from "ui/screens/translation-managers";
import { IPickerSearchCriterion, applyPickerFilter } from "ui/shared/helpers/plex-components";
import { extractApiErrorMessage } from "ui/shared/models/common";

import { messageRelationshipsActions } from "./MessageRelationships.actions";
import { getNotLinkedMessages, getRelationships, addRelationship, deleteRelationship, getMessageDetails } from "./MessageRelationships.api";
import { IMessage, IRelationship } from "./MessageRelationships.state";

function* loadMessageDetailsExecute({ payload }: PayloadAction<string>) {
  try {
    const messageDetails: IMessage = yield call(getMessageDetails, payload);
    yield put(
      messageRelationshipsActions.loadMessageDetailsCompleted({
        message: messageDetails,
        notifications: messageDetails.parentMessage
          ? [notification.warning().useMessage({ messageId: displayMessages.warn("messageHasParent"), tokens: { messageId: messageDetails.messageId } })]
          : []
      })
    );
  } catch (error) {
    yield put(messageRelationshipsActions.loadMessageDetailsFailed(notification.error(error).useMessageId(commonMessages.error("unexpectedError"))));
  }
}

function* loadMessagesWithoutRelationshipsExecute({ payload }: PayloadAction<IPickerSearchCriterion<IMessage>>) {
  try {
    let messages: IMessage[] = yield call(getNotLinkedMessages, payload.query.query);
    messages = applyPickerFilter(messages, payload, row => row.messageId);
    yield put(messageRelationshipsActions.loadPickerMessagesCompleted(messages));
  } catch (error) {
    yield put(messageRelationshipsActions.loadPickerMessagesFailed(notification.error(error).useMessageId(commonMessages.error("unexpectedError"))));
  }
}

function* loadRelationshipsExecute({ payload }: PayloadAction<string>) {
  try {
    const relationships: IMessage[] = yield call(getRelationships, payload);
    yield put(messageRelationshipsActions.loadRelationshipsCompleted(relationships));
  } catch (error) {
    yield put(messageRelationshipsActions.loadRelationshipsFailed(notification.error(error).useMessageId(commonMessages.error("unexpectedError"))));
  }
}

function* addRelationshipExecute({
  payload
}: PayloadAction<{
  messageId: string;
  version: number;
  newRelationship: IMessage;
  redirect: boolean;
}>) {
  const newRelationship = payload.newRelationship;
  try {
    const successMessage = notification.create(BannerStatus.success).useMessageId(displayMessages.info("addRelationshipCompleted"));
    yield call(addRelationship, payload.messageId, newRelationship.messageId, payload.version);
    yield put(
      messageRelationshipsActions.addRelationshipCompleted({
        notification: successMessage,
        newRelationship: newRelationship,
        redirect: payload.redirect
      })
    );
  } catch (error) {
    yield put(
      messageRelationshipsActions.addRelationshipFailed(
        error.statusCode === HttpStatusCodes.BadRequest
          ? notification.error(error).useMessage(extractApiErrorMessage(error.data).localizable)
          : notification.error(error).useMessageId(commonMessages.error("unexpectedError"))
      )
    );
  }
}

function* deleteRelationshipExecute({ payload }: PayloadAction<IRelationship>) {
  const messageId: string = payload.isParentMessage ? payload.messageId : payload.relationshipMessageId;
  const childMessageId: string = payload.isParentMessage ? payload.relationshipMessageId : payload.messageId;
  const childMessageVersion: number = payload.isParentMessage ? payload.messageVersion : payload.relationshipMessageVersion;

  try {
    yield call(deleteRelationship, messageId, childMessageId, childMessageVersion);
    yield put(
      messageRelationshipsActions.deleteRelationshipCompleted({
        message: notification.create(BannerStatus.success).useMessageId(displayMessages.info("deleteRelationshipCompleted")),
        version: payload.isParentMessage ? payload.messageVersion : payload.messageVersion + 1,
        removedRelationshipId: payload.relationshipMessageId
      })
    );
  } catch (error) {
    yield put(
      messageRelationshipsActions.deleteRelationshipFailed(
        error.statusCode === HttpStatusCodes.BadRequest
          ? notification.error(error).useMessage(extractApiErrorMessage(error.data).localizable)
          : notification.error(error).useMessageId(commonMessages.error("unexpectedError"))
      )
    );
  }
}

function* actionWatcher() {
  yield takeLatest(messageRelationshipsActions.loadMessageDetails.toString(), loadMessageDetailsExecute);
  yield takeLatest(messageRelationshipsActions.loadRelationships.toString(), loadRelationshipsExecute);
  yield takeLatest(messageRelationshipsActions.loadPickerMessages.toString(), loadMessagesWithoutRelationshipsExecute);
  yield takeLatest(messageRelationshipsActions.addRelationship.toString(), addRelationshipExecute);
  yield takeLatest(messageRelationshipsActions.deleteRelationship.toString(), deleteRelationshipExecute);
}

export function* appSaga() {
  yield all([actionWatcher()]);
}
